/* eslint-disable no-shadow */
import React, {
  useState,
  useCallback,
  useMemo,
  useEffect,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  Row, Divider, Table, Select, Badge,
} from 'antd';
import PropTypes from 'prop-types';

import { useTranslation } from 'react-i18next';

import SageIntacctImportDrawer from './SageIntacctImportDrawer';
import ConnectToSageIntacct from './ConnectToSageIntacct';

import CheckRows from '../../CheckRows';
import CompanyEditRow from '../../CompanyEditRow';

import OnTraccrButton from '../../../common/buttons/OnTraccrButton';
import OnTraccrTextInput from '../../../common/inputs/OnTraccrTextInput';
import DisplayText from '../../../common/text/DisplayText';
import CustomConfirmModal from '../../../common/modals/CustomConfirmModal';
import DivisionSelector from '../../../common/inputs/DivisionSelector';

import {
  getSageIntacctData,
  getSageIntacctEntities,
  disconnectSageIntacct,
  importIntegrationData,
  updateSageIntacctIntegration,
} from '../../state/settings.actions';

import { getUsers } from '../../../users/state/users.actions';
import { getProjects } from '../../../projects/state/projects.actions';
import { getCustomers } from '../../../contacts/customers/state/customers.actions';
import { getVendors } from '../../../contacts/vendors/state/vendors.actions';
import { getEquipment } from '../../../equipment/state/equipment.actions';
import { getMaterials } from '../../../materials/state/materials.actions';

import Colors from '../../../constants/Colors';

import Permissions from '../../../auth/Permissions';

const flavourText = `
Deactivating your Sage Intacct integration will disable any
further syncing between Ontraccr and Sage Intacct.
`;

const DIVIDER_STYLE = {
  margin: 0,
  backgroundColor: Colors.ONTRACCR_OPACITY_GRAY,
  maxWidth: 440,
  minWidth: 440,
};

function SageIntacctIntegration({
  onClose,
  selectedIntegration,
  onIntegrationChanged,
  isSingleDiv,
}) {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const divisions = useSelector((state) => state.settings.divisions);
  const sageIntacctEmployees = useSelector((state) => state.users.sageIntacctEmployees);
  const sageIntacctProjects = useSelector((state) => state.projects.sageIntacctProjects);
  const sageIntacctCustomers = useSelector((state) => state.customers.sageIntacctCustomers);
  const sageIntacctVendors = useSelector((state) => state.vendors.sageIntacctVendors);
  const sageIntacctEquipment = useSelector((state) => state.equipment.sageIntacctEquipment);
  const sageIntacctMaterials = useSelector((state) => state.materials.sageIntacctMaterials);
  const sageIntacctEntities = useSelector((state) => state.settings.sageIntacctEntities);
  const equipmentStatuses = useSelector((state) => state.equipment.statuses);
  const company = useSelector((state) => state.settings.company);
  const {
    userId: ownerId,
  } = company ?? {};

  const [showSync, setShowSync] = useState(false);
  const [importDrawerLoading, setImportDrawerLoading] = useState(false);
  const [syncLoading, setSyncLoading] = useState(false);

  // Check if the integration is connected via OAuth
  const isConnected = useMemo(() => (
    selectedIntegration?.isConnected === true
  ), [selectedIntegration?.isConnected]);

  const onIntegrationUpdate = useCallback((newData) => {
    if (!isSingleDiv && onIntegrationChanged) {
      onIntegrationChanged(newData);
    } else if (selectedIntegration?.id) {
      const {
        id,
        isConnected,
        isExpired,
        tokenExpiration,
        needsAuthorization,
        ...details
      } = selectedIntegration;
      const fullDetails = {
        ...details,
        ...newData,
      };
      dispatch(updateSageIntacctIntegration(id, fullDetails));
    }
  }, [onIntegrationChanged, selectedIntegration, isSingleDiv]);

  const onDivisionsChanged = useCallback((newDivisions) => {
    const newData = {
      divisions: newDivisions,
    };
    if (!selectedIntegration?.defaultDivisionId && newDivisions.length > 0) {
      [newData.defaultDivisionId] = newDivisions;
    } else if (selectedIntegration?.defaultDivisionId
      && !newDivisions.includes(selectedIntegration?.defaultDivisionId)) {
      // Need to clear default division ID if its not in division list
      newData.defaultDivisionId = newDivisions.length ? newDivisions[0] : null;
    }
    onIntegrationUpdate(newData);
  }, [onIntegrationUpdate, selectedIntegration]);

  const onSageIdChanged = useCallback((divisionId, sageId) => {
    const newDivisionSageIds = {
      ...selectedIntegration?.divisionSageIds,
      [divisionId]: sageId,
    };
    onIntegrationUpdate({ divisionSageIds: newDivisionSageIds });
  }, [onIntegrationUpdate, selectedIntegration]);

  const divisionTableData = useMemo(() => (
    (selectedIntegration?.divisions || []).map((divisionId) => ({
      key: divisionId,
      divisionId,
      divisionName: divisions[divisionId]?.name || divisionId,
      sageId: selectedIntegration?.divisionSageIds?.[divisionId] || '',
    }))
  ), [selectedIntegration?.divisions, selectedIntegration?.divisionSageIds, divisions]);

  const entityOptions = useMemo(() => (
    sageIntacctEntities?.map((ent) => ({
      value: ent.sageId,
      label: ent.name,
    })) ?? []
  ), [sageIntacctEntities]);

  const equipmentStatusOptions = useMemo(() => (
    Object.values(equipmentStatuses || {}).map((status) => ({
      value: status.id,
      label: status.status,
    }))
  ), [equipmentStatuses]);
  const divisionTableColumns = useMemo(() => [
    {
      title: 'Division',
      dataIndex: 'divisionName',
      key: 'divisionName',
    },
    {
      title: 'Sage Intacct Entity',
      dataIndex: 'sageId',
      key: 'sageId',
      render: (text, record) => (
        <Select
          value={text || undefined}
          options={entityOptions}
          onChange={(value) => onSageIdChanged(record.divisionId, value)}
          placeholder="Select an entity"
          allowClear
          style={{ width: '100%' }}
        />
      ),
    },
  ], [onSageIdChanged, entityOptions]);

  const showConfirm = useCallback(() => (
    CustomConfirmModal({
      title: 'Deactivate Sage Intacct Integration',
      content: (
        <p>
          Are you sure you want to your deactivate Sage Intacct integration?
          <br />
          <br />
          {flavourText}
        </p>
      ),
      async onOk() {
        if (await dispatch(disconnectSageIntacct(selectedIntegration?.id))) {
          if (onClose) onClose();
        }
      },
      okText: 'Deactivate',
    })
  ), [selectedIntegration, onClose]);

  const loadData = () => dispatch(getSageIntacctData(selectedIntegration?.id));

  const divisionFilter = useMemo(() => (
    new Set(selectedIntegration?.divisions ?? [])
  ), [selectedIntegration]);

  useEffect(() => {
    if (selectedIntegration?.id
      && isConnected
      && !sageIntacctEntities?.length
    ) {
      dispatch(getSageIntacctEntities(selectedIntegration.id));
    }
  }, [
    selectedIntegration?.id,
    isConnected,
    sageIntacctEntities?.length,
  ]);

  const isValidIntegration = (
    isConnected
    && selectedIntegration?.divisions?.length > 0
  );

  return (
    <div style={{ width: '100%' }}>
      {
        isSingleDiv && !selectedIntegration?.id
        && (
          <ConnectToSageIntacct />
        )
      }
      {!isSingleDiv
      && (
      <div style={{ width: '100%', padding: 10 }}>
        <DisplayText
          title="Divisions"
          style={{ fontFamily: 'roboto-medium', marginBottom: 0 }}
        />
        <DisplayText
          title="Select the divisions to link with this Sage Intacct integration"
          style={{
            fontFamily: 'roboto-regular',
            color: Colors.ONTRACCR_OPACITY_GRAY,
            marginBottom: 0,
            maxWidth: 400,
          }}
        />
        <Row justify="start">
          <DivisionSelector
            divisions={selectedIntegration?.divisions}
            onChange={onDivisionsChanged}
            mode="multiple"
            style={{ width: '100%', margin: 10, maxWidth: 400 }}
          />
        </Row>
        {selectedIntegration?.id && selectedIntegration?.divisions?.length > 0 && (
          <div style={{ marginTop: 16, marginBottom: 16 }}>
            <DisplayText
              title="Link divisions to Sage Intacct Entities"
              style={{
                fontFamily: 'roboto-regular',
                color: Colors.ONTRACCR_OPACITY_GRAY,
                marginBottom: 10,
              }}
            />
            <Table
              dataSource={divisionTableData}
              columns={divisionTableColumns}
              pagination={false}
              size="small"
              style={{ maxWidth: 440 }}
            />
          </div>
        )}
        <Divider style={DIVIDER_STYLE} />
      </div>
      )}

      {
        selectedIntegration?.id
          && (
            <CompanyEditRow
              title="Import from Sage Intacct"
              helpText="Import Employees, Projects, Customers, Vendors, Cost Codes, and Locations from Sage Intacct into Ontraccr."
            >
              <OnTraccrButton
                title="Set Up"
                style={{ marginRight: 45 }}
                loading={importDrawerLoading}
                disabled={importDrawerLoading || !isValidIntegration}
                onClick={async () => {
                  setImportDrawerLoading(true);
                  if (await loadData()) {
                    setShowSync(true);
                  }
                  setImportDrawerLoading(false);
                }}
              />
            </CompanyEditRow>
          )
      }

      <div style={{ maxWidth: 450 }}>
        {selectedIntegration?.id && (
          <>
            <Divider style={DIVIDER_STYLE} />
            <CompanyEditRow
              title="Connection Status"
              divider={false}
            >
              <Row align="middle" style={{ paddingRight: 45 }}>
                <Badge
                  color={isConnected ? 'green' : 'red'}
                  style={{ paddingRight: 10 }}
                />
              </Row>
            </CompanyEditRow>
          </>
        )}
      </div>

      {selectedIntegration?.id && (
        <>
          <CheckRows
            isChecked={({ key }) => selectedIntegration?.[key]}
            onChange={({ key }) => onIntegrationUpdate({
              [key]: !selectedIntegration?.[key],
            })}
            data={[
              {
                key: 'pullEnabled',
                title: 'Auto-Sync from Sage Intacct to Ontraccr',
                divider: true,
                helpText: (
                  <div style={{ width: 300 }}>
                    Auto-Sync from Sage Intacct to Ontraccr will
                    automatically import any new changes you make in Sage Intacct
                    into Ontraccr Users,
                    {' '}
                    {t('Project', { count: 2 })}
                    , Customers, and Vendors.
                  </div>
                ),
                useSwitch: true,
              },
            ]}
          />
          <CheckRows
            isChecked={({ key }) => selectedIntegration?.[key]}
            onChange={({ key }) => onIntegrationUpdate({
              [key]: !selectedIntegration?.[key],
            })}
            data={[
              {
                key: 'pushEnabled',
                title: 'Auto-Sync from Ontraccr to Sage Intacct',
                divider: true,
                helpText: (
                  <div style={{ width: 300 }}>
                    Auto-Sync from Ontraccr to Sage Intacct will
                    automatically push any new changes you make in Ontraccr
                    into Sage Intacct Employees,
                    {' '}
                    {t('Project', { count: 2 })}
                    , Customers, and Vendors.
                  </div>
                ),
                useSwitch: true,
              },
            ]}
          />
        </>
      )}

      {!isSingleDiv && selectedIntegration?.id
      && (
      <div style={{ width: '100%', padding: 10 }}>
        <DisplayText
          title="Default Division"
          style={{ fontFamily: 'roboto-medium', marginBottom: 0 }}
        />
        <DisplayText
          title="Select the default division for Sage Intacct items to be created in."
          style={{
            fontFamily: 'roboto-regular',
            color: Colors.ONTRACCR_OPACITY_GRAY,
            marginBottom: 0,
            maxWidth: 275,
          }}
        />
        <Row justify="start">
          <DivisionSelector
            divisionId={selectedIntegration?.defaultDivisionId}
            disabled={!selectedIntegration?.divisions?.length}
            onChange={(newDivisionId) => onIntegrationUpdate({ defaultDivisionId: newDivisionId })}
            filter={divisionFilter}
            style={{ width: '100%', margin: 10, maxWidth: 400 }}
          />
        </Row>
        <Divider style={DIVIDER_STYLE} />
      </div>
      )}

      {selectedIntegration?.id && (
      <div style={{ width: '100%', padding: 10 }}>
        <DisplayText
          title="Default Entity"
          style={{ fontFamily: 'roboto-medium', marginBottom: 0 }}
        />
        <Row justify="start">
          <Select
            value={selectedIntegration?.defaultLocationId}
            options={entityOptions}
            onChange={(newEntityId) => onIntegrationUpdate({ defaultLocationId: newEntityId })}
            style={{ width: '100%', margin: 10, maxWidth: 400 }}
            placeholder="Select an entity"
            allowClear
          />
        </Row>
        <Divider style={DIVIDER_STYLE} />
      </div>
      )}

      {selectedIntegration?.id && (
      <div style={{ width: '100%', padding: 10 }}>
        <DisplayText
          title="Default Equipment Status"
          style={{ fontFamily: 'roboto-medium', marginBottom: 0 }}
        />
        <DisplayText
          title="Select the default status for equipment imported from Sage Intacct."
          style={{
            fontFamily: 'roboto-regular',
            color: Colors.ONTRACCR_OPACITY_GRAY,
            marginBottom: 0,
            maxWidth: 400,
          }}
        />
        <Row justify="start">
          <Select
            value={selectedIntegration?.defaultEquipmentStatusId}
            options={equipmentStatusOptions}
            onChange={(statusId) => onIntegrationUpdate({ defaultEquipmentStatusId: statusId })}
            style={{ width: '100%', margin: 10, maxWidth: 400 }}
            placeholder="Select an equipment status"
            allowClear
          />
        </Row>
        <Divider style={DIVIDER_STYLE} />
      </div>
      )}

      {
      selectedIntegration?.id
      && (
        <>
          <Row style={{ minHeight: 32, width: '100%', paddingLeft: 10 }} justify="space-between" align="middle">
            <DisplayText
              title="Deactivate Sage Intacct Integration"
              style={{ fontFamily: 'roboto-medium', marginBottom: 0 }}
            />
          </Row>
          <DisplayText
            title={flavourText}
            style={{
              fontFamily: 'roboto-regular',
              color: Colors.ONTRACCR_OPACITY_GRAY,
              marginBottom: 0,
              maxWidth: 275,
              paddingLeft: 10,
            }}
          />

          <Row style={{ width: '100%', marginBottom: 10 }} justify="center">
            <OnTraccrButton
              title="Deactivate"
              type={Permissions.id === ownerId ? 'primary' : 'back'}
              disabled={Permissions.id !== ownerId}
              style={{ margin: 10 }}
              onClick={showConfirm}
            />
            <DisplayText
              style={{
                textAlign: 'center',
                fontFamily: 'roboto-bold',
                color: Colors.ONTRACCR_OPACITY_GRAY,
                height: 32,
              }}
              title="Only the account owner can deactivate"
            />
          </Row>
        </>
      )
      }

      <SageIntacctImportDrawer
        sageIntacctEmployees={sageIntacctEmployees}
        sageIntacctProjects={sageIntacctProjects}
        sageIntacctCustomers={sageIntacctCustomers}
        sageIntacctVendors={sageIntacctVendors}
        sageIntacctEquipment={sageIntacctEquipment}
        sageIntacctMaterials={sageIntacctMaterials}
        visible={showSync}
        divisionFilter={divisionFilter}
        width={400}
        loading={syncLoading}
        onClose={() => setShowSync(false)}
        onSave={async (res) => {
          setSyncLoading(true);
          try {
            const {
              ...payload
            } = res;
            payload.integrationKey = 'sageIntacctId';
            payload.integrationId = selectedIntegration.id;

            const {
              id,
              isConnected,
              isExpired,
              tokenExpiration,
              needsAuthorization,
              ...details
            } = selectedIntegration;
            const newIntegration = {
              ...details,
            };
            delete newIntegration.id;

            if (await dispatch(importIntegrationData(payload))) {
              if (selectedIntegration?.id
                && await dispatch(
                  updateSageIntacctIntegration(selectedIntegration.id, newIntegration),
                )) {
                if (onIntegrationChanged) onIntegrationChanged(newIntegration);
                Promise.all([
                  dispatch(getUsers(true)),
                  dispatch(getProjects()),
                  dispatch(getCustomers(true)),
                  dispatch(getVendors()),
                  dispatch(getEquipment()),
                  dispatch(getMaterials()),
                ]);
                setShowSync(false);
              }
            }
          } finally {
            setSyncLoading(false);
          }
        }}
      />
    </div>
  );
}

SageIntacctIntegration.propTypes = {
  onClose: PropTypes.func,
  selectedIntegration: PropTypes.shape({
    id: PropTypes.string.isRequired,
    defaultDivisionId: PropTypes.string,
    defaultLocationId: PropTypes.string,
    defaultEquipmentStatusId: PropTypes.string,
    divisions: PropTypes.arrayOf(PropTypes.string),
    divisionSageIds: PropTypes.objectOf(PropTypes.string),
    pullEnabled: PropTypes.bool,
    pushEnabled: PropTypes.bool,
    isConnected: PropTypes.bool,
    isExpired: PropTypes.bool,
    tokenExpiration: PropTypes.number,
    needsAuthorization: PropTypes.bool,
  }),
  onIntegrationChanged: PropTypes.func,
  isSingleDiv: PropTypes.bool,
};

SageIntacctIntegration.defaultProps = {
  selectedIntegration: null,
  onClose: null,
  onIntegrationChanged: null,
  isSingleDiv: false,
};

export default SageIntacctIntegration;
